import { differenceInHours } from "date-fns";
import { AlertTriangle, MessageCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { MyCircleBooking } from "@/hooks/useMyCircleBookings";

interface CircleCancelDialogProps {
  booking: MyCircleBooking | null;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}

export function CircleCancelDialog({
  booking,
  onClose,
  onConfirm,
  isPending,
}: CircleCancelDialogProps) {
  if (!booking) return null;

  const hoursUntilEvent = differenceInHours(
    new Date(booking.circle_event.start_time),
    new Date()
  );
  const canAutoRefund = hoursUntilEvent > 24;

  return (
    <AlertDialog open={!!booking} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {canAutoRefund ? (
              "Cancel Booking?"
            ) : (
              <>
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Late Cancellation
              </>
            )}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            {canAutoRefund ? (
              <>
                <p>
                  Are you sure you want to cancel your booking for{" "}
                  <strong>{booking.circle_event.title}</strong>?
                </p>
                <p className="text-emerald-600 font-medium">
                  You will receive a full refund of {booking.credits_spent} credit(s).
                </p>
              </>
            ) : (
              <>
                <p>
                  This event starts in less than 24 hours. Automatic refunds are not
                  available for late cancellations.
                </p>
                <p>
                  Please contact the Community Lead via WhatsApp to discuss your cancellation.
                </p>
                {booking.circle_event.whatsapp_group && (
                  <Button
                    variant="outline"
                    className="w-full mt-2"
                    asChild
                  >
                    <a
                      href={booking.circle_event.whatsapp_group}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Contact Host on WhatsApp
                    </a>
                  </Button>
                )}
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {canAutoRefund ? "Keep Booking" : "Close"}
          </AlertDialogCancel>
          {canAutoRefund && (
            <AlertDialogAction
              onClick={onConfirm}
              disabled={isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isPending ? "Cancelling..." : "Yes, Cancel Booking"}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
